// Admin API client for the program-news source registry and pipeline (ZON-269 / ZON-270 / ZON-272).
// Mirrors the req() helper in adminDashboard.ts rather than importing its private functions.

const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

/**
 * Error carrying the HTTP status so callers can branch on 403 (read-only environment)
 * or 409 (a run is already in progress). `data` is the parsed error body, when the
 * server sent one, e.g. `{ run }` on a 409.
 */
export class ApiError extends Error {
  status: number;
  data?: Record<string, unknown>;

  constructor(message: string, status: number, data?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

async function req<T>(method: string, path: string, body?: Record<string, unknown>): Promise<T> {
  const headers: Record<string, string> = {};
  if (body) headers["Content-Type"] = "application/json";

  try {
    const token = sessionStorage.getItem("recruit_ai_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch {
    // Ignore token retrieval errors
  }

  const resp = await fetch(withBase(path), {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await resp.text();
  let data: { error?: string; message?: string; raw?: string; [key: string]: unknown };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!resp.ok) {
    const msg = data?.error || data?.message || text || `Request failed (${resp.status})`;
    throw new ApiError(msg, resp.status, data);
  }
  return data as T;
}

export type ProgramSourceStatus = "verified" | "queued" | "failed" | "disabled" | "override_stale";

export type ProgramSourceCmsFamily = "sidearm" | "wmt-rss" | "clemson-html" | "unknown";

export interface ProgramSourceAttempt {
  at: string | null;
  ok: boolean;
  itemCount: number | null;
  parserVersion: number | string | null;
  durationMs: number | null;
  retryCount: number | null;
  error: string | null;
}

/** Fetch-runtime state for a source; null when the source has never been fetched. */
export interface ProgramSourceState {
  lastFetchedAt: string | null;
  lastSuccessfulAt: string | null;
  lastAttempt: ProgramSourceAttempt | null;
  consecutiveFailures: number;
}

/** Operator override set in QA; promoted to staging/prod with the data push. */
export interface ProgramSourceOverride {
  url: string;
  setBy: string | null;
  setAt: string | null;
  reason: string;
  lastVerifiedAt: string | null;
}

/** Set when an operator disabled the source on purpose (ZON-272). Absent on older API builds. */
export interface ProgramSourceDisabled {
  reason: string;
  setBy: string | null;
  setAt: string | null;
}

export interface ProgramSource {
  teamId: string;
  sourceType: string | null;
  url: string | null;
  scope: string | null;
  cmsFamily: ProgramSourceCmsFamily | null;
  verificationUrl: string | null;
  verifiedAt: string | null;
  verificationMethod: string | null;
  suggestedUrl: string | null;
  status: ProgramSourceStatus;
  state: ProgramSourceState | null;
  override: ProgramSourceOverride | null;
  /** True when the team is on an athlete's My Schools list. Absent on older API builds. */
  onList?: boolean;
  /** Why/who/when the source was disabled. Absent when not disabled or on older API builds. */
  disabled?: ProgramSourceDisabled | null;
}

export interface ProgramSourceCounts {
  verified: number;
  queued: number;
  failed: number;
  disabled: number;
  override_stale: number;
}

/**
 * School Watch coverage of the programs on athletes' lists: `working` = source
 * verified and read successfully in the last 7 days. The launch gate is 90%.
 */
export interface ProgramSourceCoverage {
  listed: number;
  withSource: number;
  verified: number;
  working: number;
  /** null when no program is on a list. */
  workingPct: number | null;
}

export interface ProgramSourceListResponse {
  /** True only in QA, where the registry is the source of truth. */
  writable: boolean;
  counts: ProgramSourceCounts;
  /** How many registry rows are on an athlete's list. Absent on older API builds. */
  onListCount?: number;
  /** Absent on older API builds. */
  coverage?: ProgramSourceCoverage;
  sources: ProgramSource[];
}

export interface SetOverrideResponse {
  source: ProgramSource;
  override: ProgramSourceOverride | null;
  state: ProgramSourceState | null;
}

export async function fetchProgramNewsSources(params: {
  status?: ProgramSourceStatus;
  limit?: number;
  /** When true, only rows on an athlete's list are returned. */
  onList?: boolean;
}): Promise<ProgramSourceListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.onList) qs.set("onList", "true");
  const query = qs.toString();
  return req<ProgramSourceListResponse>(
    "GET",
    `/admin/program-news/sources${query ? `?${query}` : ""}`
  );
}

export async function setProgramNewsSourceOverride(
  teamId: string,
  url: string,
  reason: string
): Promise<SetOverrideResponse> {
  return req<SetOverrideResponse>(
    "PUT",
    `/admin/program-news/sources/${encodeURIComponent(teamId)}/override`,
    { url, reason }
  );
}

// ---------------------------------------------------------------------------
// Pipeline operations (ZON-272): runs, articles (items), events (updates),
// disable/enable. Response fields may be missing on older API builds; callers
// degrade gracefully.
// ---------------------------------------------------------------------------

export type ProgramNewsRunType = "collect" | "classify";
export type ProgramNewsRunTrigger = "cron" | "admin" | "script";

export interface CollectCounters {
  sources?: number;
  ok?: number;
  errors?: number;
  unsupported?: number;
  listed?: number;
  newItems?: number;
  duplicates?: number;
  backfillSkipped?: number;
  zeroItemWarnings?: number;
  durationMs?: number;
}

export interface ClassifyCounters {
  items?: number;
  suppressed?: number;
  classified?: number;
  alerts?: number;
  digests?: number;
  ignores?: number;
  escalated?: number;
  gateRejected?: number;
  schemaInvalid?: number;
  failed?: number;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  projectedMonthlyUsd?: number;
  stoppedForCost?: boolean;
  durationMs?: number;
}

export type ProgramNewsRunCounters = CollectCounters & ClassifyCounters;

export type ProgramNewsRunStatus = "running" | "succeeded" | "failed";

export interface ProgramNewsRun {
  _id: string;
  type: ProgramNewsRunType;
  trigger: ProgramNewsRunTrigger;
  /** Absent on rows written before runs became asynchronous; see runStatusOf(). */
  status?: ProgramNewsRunStatus;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  /** Null while the run is still going. */
  counters: ProgramNewsRunCounters | null;
  error: string | null;
  /** Sources (collect) or articles (classify) the run was capped at; null/absent means all. */
  limit?: number | null;
  /** True for a run over everything (no cap). Missing on legacy rows means false. */
  full?: boolean;
  teamIds?: string[] | null;
  env?: string;
}

/**
 * The run's status, filling in for rows written before the API recorded one:
 * an error means failed, a finish time means succeeded, otherwise it is still running.
 */
export function runStatusOf(
  run: Pick<ProgramNewsRun, "finishedAt" | "error"> & { status?: ProgramNewsRunStatus }
): ProgramNewsRunStatus {
  if (run.status) return run.status;
  if (run.error) return "failed";
  if (run.finishedAt) return "succeeded";
  return "running";
}

export interface ProgramNewsRunsResponse {
  runs: ProgramNewsRun[];
}

export interface ProgramNewsRunResponse {
  run: ProgramNewsRun;
}

export async function fetchProgramNewsRuns(params: {
  type?: ProgramNewsRunType;
  limit?: number;
}): Promise<ProgramNewsRunsResponse> {
  const qs = new URLSearchParams();
  if (params.type) qs.set("type", params.type);
  if (params.limit) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return req<ProgramNewsRunsResponse>("GET", `/admin/program-news/runs${query ? `?${query}` : ""}`);
}

/** One run by id; polled while a manual run is in progress. */
export async function fetchProgramNewsRun(id: string): Promise<ProgramNewsRunResponse> {
  return req<ProgramNewsRunResponse>("GET", `/admin/program-news/runs/${encodeURIComponent(id)}`);
}

/**
 * Starts the collector and returns right away (202) with the run to poll.
 * 409 (ApiError with `data.run`) when a collect run is already in progress.
 */
export async function runProgramNewsCollection(
  params: { limit?: number; full?: boolean } = {}
): Promise<ProgramNewsRunResponse> {
  const body: Record<string, unknown> = {};
  // The API rejects full combined with a limit, so full wins and the limit is dropped.
  if (params.full) body.full = true;
  else if (params.limit) body.limit = params.limit;
  return req<ProgramNewsRunResponse>("POST", "/admin/program-news/collect", body);
}

/** Starts the classifier and returns right away (202) with the run to poll; 409 as above. */
export async function runProgramNewsClassification(
  params: { limit?: number; full?: boolean } = {}
): Promise<ProgramNewsRunResponse> {
  const body: Record<string, unknown> = {};
  // The API rejects full combined with a limit, so full wins and the limit is dropped.
  if (params.full) body.full = true;
  else if (params.limit) body.limit = params.limit;
  return req<ProgramNewsRunResponse>("POST", "/admin/program-news/classify", body);
}

export type ProgramNewsReviewVerdict = "right" | "wrong";

export type ProgramNewsReviewTag =
  | "wrong-school"
  | "wrong-gender"
  | "wrong-coach"
  | "stale-news"
  | "duplicate"
  | "should-not-be-suppressed"
  | "other";

export const REVIEW_TAGS: ReadonlyArray<{ value: ProgramNewsReviewTag; label: string }> = [
  { value: "wrong-school", label: "Wrong school" },
  { value: "wrong-gender", label: "Wrong gender" },
  { value: "wrong-coach", label: "Wrong coach" },
  { value: "stale-news", label: "Old news" },
  { value: "duplicate", label: "Duplicate" },
  { value: "should-not-be-suppressed", label: "Should not have been thrown out" },
  { value: "other", label: "Other" },
];

export interface ProgramNewsReview {
  _id: string;
  targetType: "update" | "item";
  targetId: string;
  verdict: ProgramNewsReviewVerdict;
  correctClass?: ProgramNewsDeliveryClass | null;
  tags: string[];
  critique?: string | null;
  reviewerEmail: string;
  /** Optional server hint that this review belongs to the requesting admin. */
  mine?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProgramNewsReviewInput {
  targetType: "update" | "item";
  targetId: string;
  verdict: ProgramNewsReviewVerdict;
  /** Required by the API when the verdict is "wrong". */
  correctClass?: ProgramNewsDeliveryClass;
  tags?: string[];
  critique?: string;
}

export type ProgramNewsItemStatus = "new" | "suppressed" | "classified" | "failed";
export type ProgramNewsDeliveryClass = "alert" | "digest" | "ignore";

export interface ProgramNewsItemClassification {
  deliveryClass?: ProgramNewsDeliveryClass | null;
  eventType?: string | null;
  confidence?: number | null;
  escalated?: boolean;
  gate?: string | null;
  models?: string[] | Record<string, unknown> | null;
  rationale?: string | null;
  error?: string | null;
}

export interface ProgramNewsItem {
  _id: string;
  teamId: string;
  title: string;
  sourceUrl: string | null;
  publishedAt: string | null;
  fetchedAt: string | null;
  processingStatus: ProgramNewsItemStatus;
  summary?: string | null;
  /** Why a rule threw the article out; null on rows suppressed before reasons were saved. */
  suppressedReason?: string | null;
  /** Machine name of the rule that suppressed it. */
  suppressedRule?: string | null;
  /** What the classifier decided; null for unclassified or legacy rows. */
  classifiedOutcome?: ProgramNewsDeliveryClass | null;
  /** The classifier's one-line reason; null for unclassified or legacy rows. */
  classificationRationale?: string | null;
  /** Extracted article text; only present on the single-item detail call. */
  permittedText?: string | null;
  /** How many reviewers have recorded a verdict. Absent on older API builds. */
  reviewCount?: number;
  /** The requesting admin's own verdict, or null. */
  myVerdict?: ProgramNewsReviewVerdict | null;
  rawMetadata?: {
    classification?: ProgramNewsItemClassification | null;
    [key: string]: unknown;
  } | null;
}

export interface ProgramNewsItemCounts {
  new: number;
  suppressed: number;
  classified: number;
  failed: number;
}

export interface ProgramNewsItemsResponse {
  counts: Partial<ProgramNewsItemCounts>;
  items: ProgramNewsItem[];
}

export async function fetchProgramNewsItems(params: {
  teamId?: string;
  status?: ProgramNewsItemStatus;
  reviewed?: "yes" | "no";
  limit?: number;
}): Promise<ProgramNewsItemsResponse> {
  const qs = new URLSearchParams();
  if (params.reviewed) qs.set("reviewed", params.reviewed);
  if (params.teamId) qs.set("teamId", params.teamId);
  if (params.status) qs.set("status", params.status);
  if (params.limit) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return req<ProgramNewsItemsResponse>(
    "GET",
    `/admin/program-news/items${query ? `?${query}` : ""}`
  );
}

export interface ProgramNewsPerson {
  name: string;
  role?: string | null;
}

export interface ProgramNewsSourceItemRef {
  _id: string;
  title?: string | null;
  sourceUrl?: string | null;
  publishedAt?: string | null;
}

export interface ProgramNewsUpdate {
  _id: string;
  teamId: string;
  type: string | null;
  title: string;
  summary?: string | null;
  eventDate?: string | null;
  discoveredAt?: string | null;
  deliveryClass: ProgramNewsDeliveryClass;
  confidence?: number | null;
  people?: ProgramNewsPerson[];
  primarySourceUrl?: string | null;
  /** Set when the event was verified against the article body. */
  evidenceHash?: string | null;
  status?: string | null;
  /** Why the classifier chose this class; null on legacy rows. */
  rationale?: string | null;
  sourceItemIds?: Array<ProgramNewsSourceItemRef | string>;
  reviewCount?: number;
  myVerdict?: ProgramNewsReviewVerdict | null;
}

export interface ProgramNewsUpdateCounts {
  alert: number;
  digest: number;
  ignore: number;
}

export interface ProgramNewsUpdatesResponse {
  counts: Partial<ProgramNewsUpdateCounts>;
  updates: ProgramNewsUpdate[];
}

export async function fetchProgramNewsUpdates(params: {
  teamId?: string;
  deliveryClass?: ProgramNewsDeliveryClass;
  status?: string;
  reviewed?: "yes" | "no";
  limit?: number;
}): Promise<ProgramNewsUpdatesResponse> {
  const qs = new URLSearchParams();
  if (params.reviewed) qs.set("reviewed", params.reviewed);
  if (params.teamId) qs.set("teamId", params.teamId);
  if (params.deliveryClass) qs.set("deliveryClass", params.deliveryClass);
  if (params.status) qs.set("status", params.status);
  if (params.limit) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return req<ProgramNewsUpdatesResponse>(
    "GET",
    `/admin/program-news/updates${query ? `?${query}` : ""}`
  );
}

export interface SourceToggleResponse {
  source: ProgramSource;
}

/** Turns a source off on purpose. 403 (ApiError) when the registry is read-only here. */
export async function disableProgramNewsSource(
  teamId: string,
  reason: string
): Promise<SourceToggleResponse> {
  return req<SourceToggleResponse>(
    "PUT",
    `/admin/program-news/sources/${encodeURIComponent(teamId)}/disable`,
    { reason }
  );
}

export async function enableProgramNewsSource(teamId: string): Promise<SourceToggleResponse> {
  return req<SourceToggleResponse>(
    "PUT",
    `/admin/program-news/sources/${encodeURIComponent(teamId)}/enable`
  );
}

export interface ProgramNewsSourceItemDetail extends ProgramNewsSourceItemRef {
  permittedText?: string | null;
  teamId?: string;
}

export interface ProgramNewsUpdateDetailResponse {
  update: ProgramNewsUpdate;
  sourceItems: ProgramNewsSourceItemDetail[];
  reviews: ProgramNewsReview[];
}

export interface ProgramNewsItemDetailResponse {
  item: ProgramNewsItem;
  reviews: ProgramNewsReview[];
}

export async function fetchProgramNewsUpdate(id: string): Promise<ProgramNewsUpdateDetailResponse> {
  return req<ProgramNewsUpdateDetailResponse>(
    "GET",
    `/admin/program-news/updates/${encodeURIComponent(id)}`
  );
}

export async function fetchProgramNewsItem(id: string): Promise<ProgramNewsItemDetailResponse> {
  return req<ProgramNewsItemDetailResponse>(
    "GET",
    `/admin/program-news/items/${encodeURIComponent(id)}`
  );
}

/** Upserts the requesting admin's review of one event or article. */
export async function saveProgramNewsReview(
  input: ProgramNewsReviewInput
): Promise<{ review: ProgramNewsReview }> {
  return req<{ review: ProgramNewsReview }>(
    "PUT",
    "/admin/program-news/reviews",
    input as unknown as Record<string, unknown>
  );
}

export async function deleteProgramNewsReview(id: string): Promise<{ ok: true }> {
  return req<{ ok: true }>("DELETE", `/admin/program-news/reviews/${encodeURIComponent(id)}`);
}
